import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  MerchantDto,
  MerchantPermissionsPatchDto,
} from "@/features/merchant/api/dto";

/**
 * Met à jour les permissions granulaires d'un marchand
 * (`PATCH /Merchants/{id}/permissions?actingMerchantId=...&updatedByKeycloakId=...`).
 * Corps `{ permissions: [{ permissionCode, isGranted }] }` — codes
 * canoniques `MERCHANT_PERMISSION_CODES` (annexe A).
 */
export const usePatchMerchantPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchants", "patch-permissions"],
    mutationFn: ({
      merchantId,
      actingMerchantId,
      updatedByKeycloakId,
      body,
    }: {
      merchantId: string;
      actingMerchantId?: string;
      updatedByKeycloakId?: string;
      body: MerchantPermissionsPatchDto;
    }) => {
      const params = new URLSearchParams();
      if (actingMerchantId) params.set("actingMerchantId", actingMerchantId);
      if (updatedByKeycloakId)
        params.set("updatedByKeycloakId", updatedByKeycloakId);
      const query = params.toString();
      return mutateData({
        url: `${merchantEndpoints.merchants.permissions(merchantId)}${query ? `?${query}` : ""}`,
        reqBody: body as unknown as Record<string, unknown>,
        method: "PATCH",
      }) as Promise<MerchantDto>;
    },
    onSuccess: (_data, { merchantId }) => {
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(merchantId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `usePatchMerchantPermissions failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
